/* eslint-disable no-param-reassign */
import React, { useRef, useState, useEffect } from 'react';
import classnames from 'classnames';
import { Card } from 'tea-component';
import Filter from '@/pages/intelligence/components/Filter';
import { TYPES_MAPPING } from '@/pages/intelligence/components/ComposedCountrySelect/constants';
import useFilterExtend from '@/pages/intelligence/hooks/useFilterExtend';
import { getStoresWhenCountryTypeChange, getMergedRegionsOptionsAndAllCountriesByStores } from '../../utils';
import { getInitialFormValue } from './utils';
import {
  // 单选、多选组合国家筛选器
  COMPOSED_COUNTRY_SELECT as COMPOSED_COUNTRY_SELECT_FILTER,
  // 单选榜单筛选器
  SINGLE_SOURCE_SELECT as SINGLE_SOURCE_SELECT_FILTER,
  // 多选榜单筛选器
  MULTIPLE_SOURCE_SELECT as MULTIPLE_SOURCE_SELECT_FILTER,
  // 是否显示热度国家筛选器
  HEAT_MAP as HEAT_MAP_SELECT_FILTER,
} from '@/pages/intelligence/components/Filter/FilterBase/_config';
import { DEFAULT_FILTER_PARAMS } from '../../StoreRank/config';
import { DEFAULT_SINGLE_COUNTRY, DEFAULT_FORM_VALUE, STATIC_FILTER_CONFIGS } from './config';

import type { IBaseProps, IRegion } from '@/pages/intelligence/types/common';
import type { IComposedCountryValue } from '@/pages/intelligence/components/ComposedCountrySelect/types';

interface IStoreRankFilter {
  selectedType: 'single' | 'multiple' | (string & {});
  regionValue: string[];
  dataSource: string[];
  showHeatMap: boolean;
}

interface IStoreRankFilterProps extends IBaseProps {
  // 筛选器右侧的info提示
  iconText?: React.ReactNode;
  isOperate: boolean;
  dataSource?: string[];
  regionsOptionsMap?: {
    [key: string]: IRegion[];
  };
  regionsOptions?: IRegion[];
  setRegionsOptions?: React.Dispatch<React.SetStateAction<IRegion[]>>;
  allCountries: string[];
  onConfirm?: (payload: IStoreRankFilter) => void;
  onReset?: () => void;
}

const StoreRankFilter: React.FC<IStoreRankFilterProps> = (props) => {
  const {
    style,
    className,
    iconText,
    isOperate,
    dataSource,
    regionsOptionsMap,
    regionsOptions,
    setRegionsOptions,
    allCountries,
    onConfirm,
    onReset,
  } = props;

  /**
   * 继承逻辑在这写
   * formValue改成用ref，可提供给父组件回填时间， 或添加继承逻辑提供formValue给父组件
   */
  const { filterExtend, setFilterExtend, resetFilterExtend } = useFilterExtend({});

  // 筛选器的初始值
  const initialFormValue = getInitialFormValue(isOperate, filterExtend, DEFAULT_FORM_VALUE);

  // 筛选器UI渲染需要的value
  const formValue = useRef<any>(initialFormValue);

  // 动态筛选器配置，即全部或者部分配置 需要通过组件props、组件其他state、组件其他变量来决定，不能单独写在一个js文件中
  // const dynamicFilterConfigs = {};

  // 筛选器控件的配置
  const [inputList, setInputList] = useState([
    STATIC_FILTER_CONFIGS.COMPOSED_COUNTRY_SELECT_FILTER,
    STATIC_FILTER_CONFIGS.SINGLE_SOURCE_SELECT_FILTER,
    STATIC_FILTER_CONFIGS.MULTIPLE_SOURCE_SELECT_FILTER,
    STATIC_FILTER_CONFIGS.HEAT_MAP_SELECT_FILTER,
  ]);

  // 更新组合单选、多选国家筛选器控件配置
  const updateComposedCountryFilterCfg = (inputList: any[], cfg: { options?: IRegion[] }) => {
    const filterCfg = inputList?.find((item) => item?.key === COMPOSED_COUNTRY_SELECT_FILTER);
    if (!filterCfg) return;
    const { options } = cfg || {};
    filterCfg.options = Array.isArray(options) ? options : [];
  };

  // 更新组合单选、多选国家筛选器value
  const updateComposedCountryFilterValue = (isOperate: boolean, allCountries: string[]) => {
    if (isOperate) return;
    formValue.current[COMPOSED_COUNTRY_SELECT_FILTER].value.type = 'multiple';
    formValue.current[COMPOSED_COUNTRY_SELECT_FILTER].value.countries = Array.isArray(allCountries) ? allCountries : [];
  };

  /**
   * 更新各个筛选器的控件配置
   */
  useEffect(() => {
    const inputListTmp = [...inputList];
    // 更新组合单选、多选国家筛选器控件配置
    updateComposedCountryFilterCfg(inputListTmp, { options: regionsOptions });
    setInputList(inputListTmp);
  }, [regionsOptions, allCountries]);

  /**
   * 更新各个筛选器的控件value
   */
  useEffect(() => {
    // 更新组合单选、多选国家筛选器value
    updateComposedCountryFilterValue(isOperate, allCountries);
  }, [isOperate, allCountries]);

  // 组合单选、多选国家筛选器 change的回调
  const handleComposedCountryChange = (
    valuePayload: IComposedCountryValue,
    inputListPayload: any[],
    formValuePayload: any,
  ) => {
    const singleSourceFilterCfg = inputListPayload?.find((item) => item?.key === SINGLE_SOURCE_SELECT_FILTER);
    const multipleSourceFilterCfg = inputListPayload?.find((item) => item?.key === MULTIPLE_SOURCE_SELECT_FILTER);
    const composedCountryFormValue = formValue?.current?.[COMPOSED_COUNTRY_SELECT_FILTER]?.value;
    const composedCountryTypeFormValue = composedCountryFormValue?.type;
    const composedCountryCountriesFormValue = composedCountryFormValue?.countries;
    const composedCountryCountryFormValue = composedCountryCountriesFormValue?.includes(DEFAULT_SINGLE_COUNTRY) ? [DEFAULT_SINGLE_COUNTRY] : composedCountryCountriesFormValue?.[0];
    const composedCountryTypePayload = valuePayload?.type;

    if (composedCountryTypePayload === composedCountryTypeFormValue) return;
    if (!singleSourceFilterCfg || !multipleSourceFilterCfg) return;

    const dataSourcesTmp = getStoresWhenCountryTypeChange(dataSource, {
      isOperate,
      countryType: composedCountryTypePayload,
      defaultSingleStore: DEFAULT_FILTER_PARAMS.dataSourceSim?.[0],
      defaultMultipleStores: DEFAULT_FILTER_PARAMS.dataSourceMul,
    });
    const { regionsOptions, allCountries } = getMergedRegionsOptionsAndAllCountriesByStores(
      dataSourcesTmp,
      regionsOptionsMap,
    );
    setRegionsOptions?.(regionsOptions);

    if (composedCountryTypePayload === TYPES_MAPPING.SINGLE) {
      // 单国家 展示多榜单
      singleSourceFilterCfg.visible = 2;
      multipleSourceFilterCfg.visible = 1;
      formValuePayload[MULTIPLE_SOURCE_SELECT_FILTER].value = dataSourcesTmp;
      formValuePayload[COMPOSED_COUNTRY_SELECT_FILTER].value.countries = allCountries?.includes(
        composedCountryCountryFormValue,
      )
        ? [composedCountryCountryFormValue]
        : DEFAULT_FILTER_PARAMS.regionValue;
    } else {
      // 多国家 展示单榜单
      singleSourceFilterCfg.visible = 1;
      multipleSourceFilterCfg.visible = 2;
      formValuePayload[SINGLE_SOURCE_SELECT_FILTER].value = dataSourcesTmp?.[0];
      formValuePayload[COMPOSED_COUNTRY_SELECT_FILTER].value.countries = allCountries;
    }
  };

  // 多选榜单筛选器 change的回调
  const handleMultipleSourceChange = (valuePayload: string[], inputListPayload: any[], formValuePayload: any) => {
    const { regionsOptions, allCountries } = getMergedRegionsOptionsAndAllCountriesByStores(
      valuePayload,
      regionsOptionsMap,
    );
    setRegionsOptions?.(regionsOptions);
    const composedCountryFormValue = formValue?.current?.[COMPOSED_COUNTRY_SELECT_FILTER]?.value;
    const composedCountryTypeFormValue = composedCountryFormValue?.type;
    const composedCountryCountriesFormValue = composedCountryFormValue?.countries;
    if (composedCountryTypeFormValue === TYPES_MAPPING.SINGLE) {
      const composedCountryCountryFormValue = composedCountryCountriesFormValue?.[0];
      formValuePayload[COMPOSED_COUNTRY_SELECT_FILTER].value.countries = allCountries?.includes(
        composedCountryCountryFormValue,
      )
        ? [composedCountryCountryFormValue]
        : DEFAULT_FILTER_PARAMS.regionValue;
    } else {
      formValuePayload[COMPOSED_COUNTRY_SELECT_FILTER].value.countries = allCountries;
    }
  };

  // 单选榜单筛选器 change的回调
  const handleSingleSourceChange = (valuePayload: string, inputListPayload: any[], formValuePayload: any) => {
    const valueTmp = valuePayload ? [valuePayload] : [];
    handleMultipleSourceChange(valueTmp, inputListPayload, formValuePayload);
  };

  // 筛选器控件change的回调，（不是confirm）
  const handleChange = (keyPayload: string, valuePayload: any, formValuePayload: any) => {
    const filterValueData = valuePayload?.value;
    const inputListTmp = [...inputList];
    const formValueTmp = {
      ...formValuePayload,
    };

    if (keyPayload === COMPOSED_COUNTRY_SELECT_FILTER) {
      handleComposedCountryChange(filterValueData, inputListTmp, formValueTmp);
    }
    if (keyPayload === SINGLE_SOURCE_SELECT_FILTER) {
      handleSingleSourceChange(filterValueData, inputListTmp, formValueTmp);
    }
    if (keyPayload === MULTIPLE_SOURCE_SELECT_FILTER) {
      handleMultipleSourceChange(filterValueData, inputListTmp, formValueTmp);
    }

    formValue.current = {
      ...formValue?.current,
      ...formValueTmp,
    };
    setInputList(inputListTmp);
  };

  /**
   * 筛选器confirm的回调
   * @param value 筛选器数据（筛选器中各个组件UI渲染需要的value）
   * @param apiValue 处理后的筛选器数据，为了更方便调用接口，针对高频特殊筛选器的数据做了数据格式转换（将筛选器原始的数据转换为更方便接口入参的数据格式，同时也会保留筛选器最原始的数据，用于筛选器继承）
   */
  const handleConfirm = (value: any, apiValue: any) => {
    formValue.current = value;
    const composedCountryFilter = apiValue?.[COMPOSED_COUNTRY_SELECT_FILTER];
    const singleSourceFilter = apiValue?.[SINGLE_SOURCE_SELECT_FILTER];
    const multipleSourceFilter = apiValue?.[MULTIPLE_SOURCE_SELECT_FILTER];
    const heatMapFilter = apiValue?.[HEAT_MAP_SELECT_FILTER];
    const selectedType = composedCountryFilter?.value?.type;
    const regionValue = composedCountryFilter?.value?.countries;
    const dataSource = selectedType === 'single' ? multipleSourceFilter?.value : [singleSourceFilter?.value];
    const showHeatMap = heatMapFilter?.value || false;
    const filterTmp = {
      selectedType,
      regionValue,
      dataSource,
      showHeatMap,
    };
    onConfirm?.(filterTmp);
    setFilterExtend(apiValue);
  };

  const handleReset = () => {
    formValue.current = {
      ...DEFAULT_FORM_VALUE,
    };
    resetFilterExtend();
    onReset?.();
  };

  return (
    <Card style={style} className={classnames('dbi-common-filter', className)}>
      <Card.Body>
        <Filter
          iconText={iconText}
          inputList={inputList}
          defaultValue={DEFAULT_FORM_VALUE}
          value={formValue.current}
          onChange={handleChange}
          onConfirm={handleConfirm}
          onReset={handleReset}
        />
      </Card.Body>
    </Card>
  );
};

export default StoreRankFilter;
